import {
  getAssignmentIdFromUrl,
  getLessonIdFromItem,
  type AllApplicationData,
} from 'course/application/general/utils/apputils';
import {SPECIAL_FIELD_NAMES} from '../../firebase/models/applicantData';
import type {
  ApplicationData,
  SectionLeadTodoItem,
  StudentTodoItem,
} from 'course/application/firebase/models/applicationData';
import type {AssignmentId} from 'assignments/types';
import type {LessonId} from 'course/lessons/lessonTypes';

/**
 * These are the items on the application that are always required even if the
 * user has requested auto accept.
 */
const ALWAYS_REQUIRED_CHECK_TYPES = [
  'aboutJob', // Everyone must read the job expectations
  'form', // Everyone must fill out the "About You" section
];

export type ApplicationStatus =
  | 'notStarted'
  | `incomplete (${number}/${number})`
  | 'complete'
  | 'withdrawn';

/**
 * Matches the incomplete status `incomplete (n/m)`, capturing `n` (checks
 * complete) and `m` (checks in total). {@link formatIncompleteStatus} is the
 * only writer of this shape, so the two must stay in step.
 */
const INCOMPLETE_STATUS_REGEX = /^incomplete \((\d+)\/(\d+)\)$/;

/**
 * Progress carried by an `incomplete (n/m)` status.
 */
export interface IncompleteProgress {
  /** Checklist items the applicant has completed. */
  nComplete: number;
  /** Checklist items the application requires in total. */
  nChecks: number;
}

/**
 * Builds the `incomplete (n/m)` status string.
 *
 * @param progress - The completed and total check counts to encode.
 * @return The status string {@link parseIncompleteProgress} reads back.
 * @throws Never.
 */
export const formatIncompleteStatus = ({
  nComplete,
  nChecks,
}: IncompleteProgress): ApplicationStatus => {
  return `incomplete (${nComplete}/${nChecks})`;
};

/**
 * Reads the progress counts out of an `incomplete (n/m)` status.
 *
 * @param appStatus - Any application status.
 * @return The counts encoded in the status, or `null` when the status is not
 *   an incomplete one (`notStarted`, `complete`, `withdrawn`).
 * @throws Never.
 */
export const parseIncompleteProgress = (
  appStatus: ApplicationStatus,
): IncompleteProgress | null => {
  const match = INCOMPLETE_STATUS_REGEX.exec(appStatus);
  if (!match) {
    return null;
  }
  return {nComplete: Number(match[1]), nChecks: Number(match[2])};
};

export const isApplicationStatus = (
  data: unknown,
): data is ApplicationStatus => {
  if (typeof data !== 'string') {
    return false;
  }
  if (INCOMPLETE_STATUS_REGEX.test(data)) {
    return true;
  }
  return ['notStarted', 'complete', 'withdrawn'].includes(data);
};

export const computeApplicationStatus = (
  allData: AllApplicationData,
  appStructure: ApplicationData,
): ApplicationStatus => {
  const allChecks = getAllChecks(appStructure);

  if (allData.appData && allData.appData['withdrawn']) {
    return 'withdrawn';
  }
  let nComplete = 0;
  for (const check of allChecks) {
    if (check(allData)) {
      nComplete += 1;
    }
  }

  if (nComplete === 0) {
    return 'notStarted';
  }
  if (nComplete < allChecks.length) {
    return formatIncompleteStatus({nComplete, nChecks: allChecks.length});
  }
  return 'complete';
};

/**
 * Picks the status an application splash should show and record. While the
 * application is open, the freshly computed status always wins. Once it has
 * closed, a recorded `complete` is final: reviewers work from the recorded
 * status, so a checklist that later computes lower (a step added after the
 * deadline, an attempt removed) must not pull a submitted application back
 * out. A withdrawal still wins, because it is the applicant's explicit choice
 * rather than a recomputation.
 *
 * @param params.computedStatus - The status {@link computeApplicationStatus}
 *   derives from the applicant's current data.
 * @param params.recordedStatus - The status already stored on the applicant's
 *   application document. Omitted or undefined (nothing recorded yet) → the
 *   computed status is returned.
 * @param params.isClosed - Whether the application no longer takes
 *   submissions.
 * @return `complete` when the application is closed, was recorded complete
 *   and has not been withdrawn; otherwise `computedStatus`.
 * @throws Never.
 */
export const resolveApplicationStatus = ({
  computedStatus,
  recordedStatus,
  isClosed,
}: {
  computedStatus: ApplicationStatus;
  recordedStatus?: ApplicationStatus;
  isClosed: boolean;
}): ApplicationStatus => {
  if (!isClosed || recordedStatus !== 'complete') {
    return computedStatus;
  }
  if (computedStatus === 'withdrawn') {
    return computedStatus;
  }
  return 'complete';
};

export const checkComplete = (
  item: StudentTodoItem | SectionLeadTodoItem,
  allAppData: AllApplicationData,
): boolean => {
  // if the user has requested auto accept, we only need the biographical data
  if (
    allAppData?.appData?.hasRequestedAutoAccept &&
    !ALWAYS_REQUIRED_CHECK_TYPES.includes(item.type)
  ) {
    return true;
  }
  // check if an about course item is complete
  if (item.type == 'aboutCourse') {
    return learnAboutCourseComplete(allAppData);
  }

  if (item.type == 'aboutJob') {
    return learnAboutJobComplete(allAppData);
  }

  if (item.type == 'lesson') {
    if (item.optional) {
      return true;
    }
    // Use the lesson id from `item.to` (where progress is recorded), not the
    // todo item's display-slug `id`, which may differ (e.g. CIPX).
    return hasLesson(allAppData, getLessonIdFromItem(item));
  }

  // check if a form item is complete
  if (item.type === 'form') {
    return hasAboutYou(allAppData);
  }

  if (item.type === 'debugging') {
    return debuggingComplete(allAppData);
  }

  if (item.type === 'simteaching') {
    return slTeachingComplete(allAppData);
  }

  if (item.type === 'sectionteaching' || item.type === 'sectionteaching-pai') {
    return sectionTeachingComplete(allAppData);
  }

  // check if a record item is complete
  if (item.type === 'record') {
    return videoComplete(allAppData);
  }

  // check if a code item is complete
  if (item.type === 'code') {
    if (item.optional) {
      return true;
    }
    const assignmentId = getAssignmentIdFromUrl(item);
    return hasAssn(allAppData, assignmentId);
  }

  return false;
};

function hasAboutYou(allData: AllApplicationData): boolean {
  return !!allData.appData?.name;
}

export function hasLesson(
  allData: AllApplicationData,
  lessonId: LessonId,
): boolean {
  return allData.lessonsProgress?.[lessonId] ?? false;
}

function hasAssn(allData: AllApplicationData, assnId: AssignmentId): boolean {
  return allData.userAssignmentProgress?.[assnId] ?? false;
}

function videoComplete(allData: AllApplicationData): boolean {
  return !!allData.videoUrl;
}

function learnAboutCourseComplete(allData: AllApplicationData): boolean {
  return allData.appData?.hasReadAboutCourse ?? false;
}

function learnAboutJobComplete(allData: AllApplicationData): boolean {
  return allData.appData?.hasReadAboutJob ?? false;
}

function debuggingComplete(allData: AllApplicationData): boolean {
  return !!allData.appData?.[SPECIAL_FIELD_NAMES.DEBUG_ANSWERS];
}

function slTeachingComplete(allData: AllApplicationData): boolean {
  return allData.slTeachingComplete ?? false;
}

function sectionTeachingComplete(allData: AllApplicationData): boolean {
  return allData.sectionTeachingComplete ?? false;
}

function getAllChecks(
  appStructure: ApplicationData,
): ((allData: AllApplicationData) => boolean)[] {
  const allChecks = [];
  for (const card of appStructure.splashCards) {
    for (const item of card.todo) {
      allChecks.push((allData: AllApplicationData) =>
        checkComplete(item, allData),
      );
    }
  }
  return allChecks;
}
